"use client"

import { useEffect, useRef, useState, useCallback } from "react"
import { createBrowserClient } from "@forestsync/auth/browser"
import type { OrderBook as OrderBookData } from "@/lib/queries"

/**
 * Hook that subscribes to Supabase Realtime changes on the trade_orders table
 * and rebuilds the order book when an INSERT, UPDATE, or DELETE occurs
 * for the given creditType + vintage.
 *
 * Falls back gracefully to the initial data if realtime is unavailable.
 */
export function useRealtimeOrderBook(
  creditType: string,
  vintage: number,
  projectRefId: string,
  initial: OrderBookData,
): OrderBookData {
  const [orderBook, setOrderBook] = useState<OrderBookData>(initial)
  const refreshTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const supabaseRef = useRef(createBrowserClient())

  // Re-fetch from the server API whenever the DB changes
  const refresh = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/internal/order-book?creditType=${encodeURIComponent(creditType)}&vintage=${vintage}&projectRefId=${encodeURIComponent(projectRefId)}`,
      )
      if (res.ok) {
        const data: OrderBookData = await res.json()
        setOrderBook(data)
      }
    } catch {
      // Silently ignore — keep last good data
    }
  }, [creditType, vintage, projectRefId])

  // Debounced refresh to avoid hammering the API on bursts of changes
  const debouncedRefresh = useCallback(() => {
    if (refreshTimeout.current) clearTimeout(refreshTimeout.current)
    refreshTimeout.current = setTimeout(refresh, 300)
  }, [refresh])

  useEffect(() => {
    // Reset when market changes
    setOrderBook(initial)

    const channel = supabaseRef.current
      .channel(`orderbook:${creditType}:${vintage}:${projectRefId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "trade_orders",
          filter: `credit_type=eq.${creditType}`,
        },
        (payload) => {
          // For DELETE events payload.new is {} (empty object, not null), so fall back to payload.old
          const raw = payload.new as Record<string, unknown> | undefined
          const row = (raw && Object.keys(raw).length > 0 ? raw : payload.old) as Record<string, unknown> | undefined
          if (!row) return
          // Only react to events for the matching vintage + project
          if (Number(row.vintage) !== vintage) return
          if (row.project_ref_id !== projectRefId) return
          debouncedRefresh()
        },
      )
      .subscribe()

    return () => {
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current)
      supabaseRef.current.removeChannel(channel)
    }
  }, [creditType, vintage, projectRefId, initial, debouncedRefresh])

  return orderBook
}
